import React, { useEffect, useMemo, useState } from 'react';
import { api, ApiError, publicAdImageUrl, publicClipImageUrl, sharedPageImageUrl } from './api';
import { longDate } from './dates';
import { IconDownload } from './Icons';
import { BannerAds, LogoBanner } from './LogoBanner';
import { FlashTicker } from './FlashNews';
import { Calendar } from './Calendar';
import { Modal } from './Modal';
import { IconCalendar } from './Icons';
import { InfoMenu } from './InfoMenu';
import { PageNav } from './PageNav';
import { PageSlides } from './PageSlides';
import { Reader } from './Reader';
import { navigate } from './router';
import { useToast } from './Toast';
import { ShareTargets } from './ShareDialog';
import { downloadBlob } from './share';
import { ClipInfo, EditionMap } from './types';

/** Opened from a shared link: starts at the shared page, every other page is a button away. */
export function PublicShare({ token }: { token: string }) {
  const [meta, setMeta] = useState<{ date: string; page: number; pageCount: number; effect?: string; sound?: string } | null>(null);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');
  const [archives, setArchives] = useState(false);
  const [ads, setAds] = useState<BannerAds | undefined>(undefined);
  const [editions, setEditions] = useState<EditionMap>({});
  const [edition, setEdition] = useState('Main');
  const toast = useToast();

  // Archive: open another day's paper in this same shared view (through its own share link), without signing in
  const openDay = (d: string) => {
    if (meta && d === meta.date) { setArchives(false); return; }
    api
      .publicDayLink(token, d)
      .then((r) => {
        setArchives(false);
        navigate('/s/' + r.token);
      })
      .catch((e) => toast(e instanceof ApiError ? e.message : 'Could not open that day.', 'error'));
  };

  useEffect(() => {
    // a new link (another day chosen from the Archive): start clean so nothing of the previous day shows
    setMeta(null);
    setErr('');
    setAds(undefined);
    setEditions({});
    setEdition('Main');
    api
      .publicShare(token)
      .then((m) => {
        setMeta(m);
        setPage(m.page);
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not open this link.'));
    api
      .publicEditions(token)
      .then(setEditions)
      .catch(() => undefined);
    api
      .publicAds(token)
      .then((list) => {
        const f = (n: number) => {
          const a = list.find((x) => x.slot === n);
          return a ? publicAdImageUrl(token, a) : undefined;
        };
        setAds({ left: f(1), right: f(2) });
      })
      .catch(() => setAds({}));
  }, [token]);

  // today's paper for the default video: the small page pictures the side strip already loads
  const video = useMemo(() => (meta ? { date: meta.date, urls: Array.from({ length: meta.pageCount }, (_, i) => sharedPageImageUrl(token, i + 1, 400)) } : null), [meta, token]);

  // same buttons as the signed-in reader: First / Previous / 3 4 5 / Next / Last and the Edition menu (+ About, Developed by)
  const nav = meta && (
    <PageNav
      date={meta.date}
      pageCount={meta.pageCount}
      page={page}
      editions={editions}
      edition={edition}
      isAdmin={false}
      hrefFor={() => '#/s/' + token}
      onGo={(n, ed) => {
        if (ed) setEdition(ed);
        setPage(n);
      }}
      onNoDistrict={(d) => toast(`${d} pages are not marked for this day's paper yet.`, 'info')}
      onEditEditions={() => undefined}
    />
  );

  return (
    <div className="app public">
      <LogoBanner ads={ads} video={video} page={page} />
      <FlashTicker pub slot={1} />
      <div className={'layout' + (meta ? ' with-slides' : '')}>
        {meta && (
          <aside className="side" aria-label="Pages">
            <PageSlides date={meta.date} pageCount={meta.pageCount} page={page} onGo={setPage} imgUrl={(n) => sharedPageImageUrl(token, n, 400)} />
          </aside>
        )}
        <main className="public-main">
          {err && <p className="error center">{err}</p>}
          {!meta && !err && <p className="muted center">Opening…</p>}
          {meta && <Reader source={{ kind: 'share', token, date: meta.date }} pageCount={meta.pageCount} page={page} onPage={setPage} nav={nav} after={<><button className="cbtn tone-rose" onClick={() => setArchives(true)} title="Archive – older papers"><IconCalendar size={18} /> <span>Archive</span></button><InfoMenu /></>} locked={{ effect: meta.effect, sound: meta.sound }} />}
        </main>
      </div>
      {archives && meta && (
        <Modal title="Archive" onClose={() => setArchives(false)}>
          <p className="muted" style={{ margin: '0 0 8px' }}>Pick a day to read that day's paper.</p>
          <Calendar
            selected={meta.date}
            version={0}
            load={(m) => api.publicMonth(token, m)}
            onPick={(d, p) => { if (p) openDay(d); }}
          />
        </Modal>
      )}
      <FlashTicker pub slot={2} />
    </div>
  );
}

export function PublicClip({ token }: { token: string }) {
  const [clip, setClip] = useState<ClipInfo | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api
      .publicClip(token)
      .then(setClip)
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not open this link.'));
  }, [token]);

  const download = async () => {
    const r = await fetch(publicClipImageUrl(token));
    downloadBlob(await r.blob(), 'clipping.jpg');
  };

  return (
    <div className="app public">
      {/* no LogoBanner here: the clipping already has the logo on top of it, so it must not be added again */}
      <FlashTicker pub slot={1} />
      <main className="public-main scroll">
        {err && <p className="error center">{err}</p>}
        {clip && (
          <div className="clip-public">
            <p className="muted center">Clipping from {longDate(clip.date)}, page {clip.page}</p>
            <img src={publicClipImageUrl(token)} alt="Newspaper clipping" />
            <div className="row center-row">
              <button className="btn" onClick={download}>
                <IconDownload size={16} /> Download
              </button>
            </div>
            <ShareTargets text="" url={window.location.href} />
          </div>
        )}
      </main>
      <FlashTicker pub slot={2} />
    </div>
  );
}
